import React from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { Check, Clock, Target, TrendingUp, FileText, Users, ChefHat, DollarSign, Package, BarChart3 } from "lucide-react";

const AuditRentabilite = () => {
  // Schema.org Service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Audit rentabilité boulangerie/pâtisserie",
    "description": "Audit terrain 2–3h + synthèse sous 48h : marge, prix, invendus, achats, mix produit, organisation. Plan d'action 7–30 jours.",
    "provider": {
      "@type": "Person",
      "name": "Christophe Marrama",
      "email": "christophe.marrama@croissanceconsulting.net",
      "telephone": "+33674245563"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Annecy, Savoie, Haute-Savoie, France"
    }
  };

  const whatWeCheck = [
    {
      icon: DollarSign,
      title: "Prix & positionnement",
      description: "Cohérence marché / qualité / ticket"
    },
    {
      icon: BarChart3,
      title: "Mix produit",
      description: "Loi 80/20, produits chronophages, produits \"aimants\""
    },
    {
      icon: TrendingUp,
      title: "Invendus & pertes",
      description: "Mesure, causes, actions rapides"
    },
    {
      icon: Package,
      title: "Achats & stocks",
      description: "Fournisseurs, ruptures, surstocks, grammages"
    },
    {
      icon: ChefHat,
      title: "Organisation & production",
      description: "Goulots, tâches, méthode simple"
    },
    {
      icon: Users,
      title: "Vitrine & vente",
      description: "Lisibilité, mise en avant marge, upsell"
    }
  ];

  const deliverables = [
    "Synthèse claire (1 page) : constats + priorités",
    "Plan d'action 7–30 jours (checklist)",
    "Rituel hebdo 15 minutes (4 indicateurs + 3 questions)",
    "Recommandations \"quick wins\" (prix / invendus / achats / gamme)"
  ];

  const expectedResults = [
    "Plus de clarté sur ce qui rapporte réellement",
    "Moins de pertes (invendus/erreurs/stock)",
    "Décisions simples sur prix/gamme/achats",
    "Entreprise plus \"vivable\" (moins de stress, plus de contrôle)"
  ];

  const faqs = [
    {
      question: "Combien de temps dure l'audit ?",
      answer: "L'immersion terrain dure 2 à 3 heures. (si la distance m'empêche de venir, par ex. DOM-TOM, possibilité de tout faire en Visio). Ensuite, je prends 24 à 48h pour analyser et te livrer la synthèse + le plan d'action. Au total, tu as un retour concret en moins d'une semaine."
    },
    {
      question: "Dois-je préparer des documents ?",
      answer: "Non, on fait simple. Si tu as des chiffres sous la main (ventes, achats, pertes), c'est un plus, mais on peut avancer sans. L'essentiel, c'est l'observation terrain et l'échange avec toi. On approfondit les données seulement si c'est utile."
    },
    {
      question: "Travaillez-vous sur place ?",
      answer: "Oui, l'immersion se fait dans ton fournil/labo/boutique pour voir la réalité du terrain. Pour la restitution, on peut faire en visio ou sur place selon tes préférences."
    },
    {
      question: "Est-ce adapté aux petites structures ?",
      answer: "Absolument. L'audit est conçu pour les artisans et petites équipes. Pas besoin d'un gros volume ou d'une équipe importante. Si tu es seul(e) ou avec 2-3 personnes, ça marche très bien."
    },
    {
      question: "Qu'est-ce que je reçois concrètement ?",
      answer: "Tu reçois : une synthèse d'une page (constats + priorités), un plan d'action 7-30 jours (checklist), un rituel hebdo de 15 minutes pour piloter, et des recommandations quick wins sur prix/invendus/achats/gamme."
    },
    {
      question: "Et si je veux aller plus loin ?",
      answer: "Après l'audit, si tu veux un accompagnement régulier, on peut enchaîner avec un pack Stabilisation & Marge (90 jours) ou Croissance Continue (mensuel). L'audit te donne déjà une bonne base pour avancer en autonomie."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Audit rentabilité boulangerie/pâtisserie | Diagnostic marge (terrain + chiffres)</title>
        <meta name="description" content="Audit terrain 2–3h + synthèse sous 48h : marge, prix, invendus, achats, mix produit, organisation. Plan d'action 7–30 jours." />
        <link rel="canonical" href="https://croissanceconsulting.net/audit-rentabilite-boulangerie" />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FBF7EF]">
        {/* Hero Section */}
        <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#4A3A2A] font-poppins text-center mb-8">
              Audit rentabilité boulangerie pâtisserie — Diagnostic immersion terrain
            </h1>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#4A3A2A] font-poppins mb-6">
                Audit rentabilité boulangerie & pâtisserie
              </h2>
              <p className="text-lg text-[#2D2D2D] leading-relaxed mb-4">
                Vous avez le sentiment de beaucoup travailler… mais que la marge ne suit pas ?
              </p>
              <p className="text-lg text-[#2D2D2D] leading-relaxed">
                L'audit rentabilité permet d'identifier rapidement où la marge fuit (prix, invendus, achats, mix produit, organisation) et de repartir avec un plan d'action simple.
              </p>
            </div>
          </div>
        </section>

        {/* À qui ça s'adresse */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-8">
              Pour quels artisans ?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#FBF7EF] rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#B65C3A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="text-[#B65C3A]" size={24} />
                </div>
                <p className="text-[#2D2D2D] font-medium">
                  Artisans déjà en activité (boulangerie / pâtisserie / snacking)
                </p>
              </div>
              <div className="bg-[#FBF7EF] rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#6B7A3A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-[#6B7A3A]" size={24} />
                </div>
                <p className="text-[#2D2D2D] font-medium">
                  Équipes petites à moyennes
                </p>
              </div>
              <div className="bg-[#FBF7EF] rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#B65C3A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="text-[#B65C3A]" size={24} />
                </div>
                <p className="text-[#2D2D2D] font-medium">
                  Dirigeants qui veulent gagner en marge sans ajouter des heures
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Ce qu'on regarde */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Ce qu'on regarde
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whatWeCheck.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 bg-[#6B7A3A]/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-[#6B7A3A]" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-[#4A3A2A] font-poppins mb-2">
                      {item.title}
                    </h3>
                    <p className="text-[#2D2D2D] text-sm">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Le déroulé */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Le déroulé
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#B65C3A] rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-2">
                    Immersion terrain (2–3h)
                  </h3>
                  <p className="text-[#2D2D2D]">
                    Observation boutique + labo + échanges
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#6B7A3A] rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-2">
                    Analyse rapide (back-office)
                  </h3>
                  <p className="text-[#2D2D2D]">
                    Tri des données utiles, repères de marge
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#B65C3A] rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#4A3A2A] font-poppins mb-2">
                    Synthèse (48h)
                  </h3>
                  <p className="text-[#2D2D2D]">
                    3–5 leviers + plan d'action 7–30 jours + rituel hebdo 15 min
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Livrables */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Livrables
            </h2>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <ul className="space-y-4">
                {deliverables.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="text-[#6B7A3A] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#2D2D2D]">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-[#4A3A2A]/10 bg-[#6B7A3A]/5 rounded-lg p-6 text-center" data-testid="audit-price-block">
                <p className="text-sm font-semibold text-[#6B7A3A] uppercase tracking-wide mb-2">
                  Tarif
                </p>
                <p className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins">
                  490 € TTC
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Résultats attendus */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              Résultats attendus
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {expectedResults.map((result, index) => (
                <div key={index} className="bg-[#6B7A3A]/5 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="text-[#6B7A3A] flex-shrink-0 mt-1" size={24} />
                    <p className="text-[#2D2D2D] font-medium">{result}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins text-center mb-12">
              FAQ
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-lg font-bold text-[#4A3A2A] font-poppins mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-[#2D2D2D] leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-3xl text-center">
            <div className="bg-[#6B7A3A]/5 rounded-2xl p-10 shadow-xl">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#4A3A2A] font-poppins mb-4">
                Prêt à reprendre la main sur votre marge ?
              </h2>
              <p className="text-lg text-[#2D2D2D] mb-8">
                Décrivez votre situation (marge, invendus, achats, organisation) et je vous réponds sous 24–48h.
              </p>
              <Link to="/contact">
                <Button className="bg-[#B65C3A] hover:bg-[#9d4e2f] text-white font-medium px-10 py-6 text-lg">
                  Proposer un échange
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AuditRentabilite;
